
import { Baby, Plus, ArrowRight, Moon, Sun, Utensils, MessageSquare } from "lucide-react"
import { Button } from "@/components/ui/button"

interface NoChildrenStateProps {
  onAddChild: () => void;
}

export const NoChildrenState = ({ onAddChild }: NoChildrenStateProps) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-8rem)] p-4 sm:p-8">
      <div className="max-w-2xl w-full">
        {/* Hero Section */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="p-4 sm:p-5 rounded-full mb-4 sm:mb-5 bg-primary/10 inline-block">
            <Baby className="w-10 h-10 sm:w-12 sm:h-12 text-primary" />
          </div>
          
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2 sm:mb-3 text-foreground">
            Welcome to Your Dashboard!
          </h2>
          
          <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8 max-w-md mx-auto">
            Let's get started by adding your first child. Once added, you'll be able to track their sleep, naps, and feeding patterns.
          </p>
          
          <Button
            onClick={onAddChild}
            size="lg"
            className="px-6 sm:px-8 py-5 sm:py-6 h-auto text-base sm:text-lg font-semibold bg-primary hover:bg-primary/90 shadow-lg"
          >
            <Plus className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
            Add Your First Child
          </Button>
        </div>
        
        {/* Features Preview */}
        <div className="bg-card/50 rounded-2xl border border-border/50 p-4 sm:p-6 backdrop-blur-sm">
          <h3 className="text-base sm:text-lg font-semibold text-foreground mb-4 text-center">
            What you'll be able to do:
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <FeatureCard 
              icon={Moon}
              title="Track Nighttime Sleep"
              description="Log sleep sessions and monitor patterns"
              color="indigo"
            />
            <FeatureCard 
              icon={Sun}
              title="Monitor Naps"
              description="Track daytime naps and durations"
              color="purple"
            />
            <FeatureCard 
              icon={Utensils}
              title="Log Feedings"
              description="Keep track of feeding schedules"
              color="pink"
            />
            <FeatureCard 
              icon={MessageSquare}
              title="Connect with Specialist"
              description="Chat with your sleep consultant"
              color="green"
            />
          </div>
        </div>
        
        {/* Quick Start Guide */}
        <div className="mt-6 sm:mt-8 text-center">
          <p className="text-xs sm:text-sm text-muted-foreground">
            <span className="font-medium">Quick tip:</span> After adding a child, use the sidebar menu to navigate between different features.
          </p>
        </div>
      </div>
    </div>
  )
}

interface FeatureCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  color: "indigo" | "purple" | "pink" | "green";
}

function FeatureCard({ icon: Icon, title, description, color }: FeatureCardProps) {
  const colorClasses = {
    indigo: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
    purple: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    pink: "bg-pink-500/10 text-pink-400 border-pink-500/20",
    green: "bg-green-500/10 text-green-400 border-green-500/20",
  }
  
  const iconColors = {
    indigo: "text-indigo-400",
    purple: "text-purple-400",
    pink: "text-pink-400",
    green: "text-green-400",
  }
  
  return (
    <div className={`flex items-start gap-3 p-3 sm:p-4 rounded-xl border ${colorClasses[color]}`}>
      <div className="flex-shrink-0">
        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${iconColors[color]}`} />
      </div>
      <div className="min-w-0">
        <h4 className="text-sm sm:text-base font-medium text-foreground truncate">{title}</h4>
        <p className="text-2xs sm:text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
  )
}
